// ============================================================
// Solution Page (Solution 2) — CLASS methodology, rebuilt for the AI age
// ============================================================
import { FileText, Link2, Layers, Users, Target, Zap, TrendingUp, BadgeCheck } from "lucide-react";

export const SOLUTION_PAGE = {
  hero: {
    eyebrow: "Our Methodology",
    titlePre: "CLASS 3.0 \u2014 ",
    titleAccent: "Rebuilt for the AI Age.",
    sub: "The same five elements that drove first-page dominance for enterprise clients \u2014 updated for a world where algorithms rank pages and AI engines choose answers.",
    bg: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=1920&auto=format&fit=crop",
  },
  intro: {
    heading: "CLASS 3.0 is such an evolved version of our methodology that we had to perform another version update.",
    body: "CLASS drove success for enterprise clients for nearly a decade of algorithm updates. CLASS 3.0 extends every element to the new arena: generative engines that read, reason and recommend. The result is a methodology designed to give you peace of mind in the most turbulent era search has ever seen.",
  },
  elements: {
    eyebrow: "The five elements",
    heading: "The future-proof methodology of CLASS 3.0",
    items: [
      {
        icon: FileText,
        letter: "C",
        title: "Content Marketing",
        line: "Answer-first content deployed by our in-house writers, designers and editors \u2014 built to rank in search and be cited by AI engines.",
      },
      {
        icon: Link2,
        letter: "L",
        title: "Link Earning",
        line: "High-authority earned media signals to linkable assets \u2014 the same trust signals generative engines weigh when choosing who to cite.",
      },
      {
        icon: Layers,
        letter: "A",
        title: "Architecture",
        line: "Impactful, prioritized technical recommendations \u2014 now including entity structure, schema and AI-readability.",
      },
      {
        icon: Users,
        letter: "S",
        title: "Signals",
        line: "Social proof, reviews and brand mentions \u2014 earned media that compounds across search engines and answer engines alike.",
      },
      {
        icon: Target,
        letter: "S",
        title: "Strategy",
        line: "Coordinated execution with our StrikingDistance\u2122 prioritization \u2014 near-wins first, so results arrive faster.",
      },
    ],
  },
  pillars: [
    {
      icon: Zap,
      title: "Powerful",
      body: "Purpose-built campaign tooling operated by US-based search experts. The scale of our capabilities ensures your campaign has the time, expertise and resources it needs \u2014 which is why agencies and even other SEO companies partner with us for their clients.",
    },
    {
      icon: TrendingUp,
      title: "Effective",
      body: "Organic search remains the most cost-effective marketing available \u2014 driving targeted traffic for pennies per visitor. Combined with GEO citations and surgical PPC, the machine consistently outperforms any single-channel budget.",
    },
    {
      icon: BadgeCheck,
      title: "Proven",
      body: "If it isn't proven, it isn't worth your money. We test and retest ranking factors every day and apply what we learn \u2014 a continued record of first-page success for completed campaigns since 2006.",
    },
  ],
  cta: {
    heading: "See CLASS 3.0 applied to your market.",
    sub: "Free Visibility Audit \u2014 your rankings, AI citations, technical architecture and the fastest path to more leads. From a US-based strategist, in 2 business days or less.",
    primaryText: "Get My Free Visibility Audit",
    secondaryText: "Talk to a Strategist",
    secondaryHref: "https://www.slingshotseo.com/contact/",
  },
};

// ---------- Solution FAQ (also powers FAQPage schema) ----------
export const SOLUTION_FAQ = {
  heading: "Questions about CLASS 3.0",
  items: [
    {
      q: "What is CLASS 3.0?",
      a: "Our methodology: Content, Link Earning, Architecture, Signals and Strategy \u2014 refined since 2006 and now extended to generative AI engines as well as search algorithms.",
    },
    {
      q: "What changed from earlier versions of CLASS?",
      a: "Every element now serves two audiences: ranking algorithms and generative engines. That means entity structure, schema, AI-readability and citation-worthy content are built into each pillar.",
    },
    {
      q: "What is StrikingDistance\u2122?",
      a: "Our prioritization method: find the keywords and AI answers where you're almost winning, close those specific gaps first, and convert near-wins into wins \u2014 so results arrive faster.",
    },
    {
      q: "Is CLASS 3.0 a one-time project or ongoing?",
      a: "Both. Architecture fixes are front-loaded; content, authority and signals compound month over month \u2014 and the optimization loop never stops.",
    },
    {
      q: "How do I know it's working?",
      a: "One revenue-first report: rankings, AI citations, traffic and cost per lead \u2014 reviewed with your US-based strategist. No vanity dashboards.",
    },
  ],
};
